import { ClientConfigurationError } from '@/lib/errors/client';

import { CHAIN_ID_PER_NETWORK_URL_PATH, SUPPORTED_CHAINS_BY_CHAIN_ID } from '@/constants/chain';

import { getBaseURL } from './environment';
import { isChainAddress } from './models';
import { TokenInfo } from './token.types';

export function getValidChainIdFromString(rawChainId: string): number {
  // 1 - Convert the full network name from the url to a valid chainId
  const chainId = CHAIN_ID_PER_NETWORK_URL_PATH[rawChainId] ?? parseInt(rawChainId, 10);

  // If the chainId is not supported, throw an error.
  if (
    typeof chainId !== 'number' ||
    Number.isNaN(chainId) ||
    !SUPPORTED_CHAINS_BY_CHAIN_ID[chainId]
  ) {
    throw new ClientConfigurationError(`Invalid chainId: ${rawChainId}`);
  }

  return chainId;
}

/**
 * Parses token metadata from scalar address and chain query parameters.
 */
export const getTokenInfoFromQueryParameter = (
  rawAddressQueryParam: string | string[] | undefined,
  rawChainIdOrNetworkNameQueryParam: string | string[] | undefined,
): TokenInfo | undefined => {
  const address =
    rawAddressQueryParam &&
    !Array.isArray(rawAddressQueryParam) &&
    isChainAddress(rawAddressQueryParam)
      ? rawAddressQueryParam
      : undefined;
  let chainId: number | undefined;

  try {
    chainId =
      rawChainIdOrNetworkNameQueryParam && !Array.isArray(rawChainIdOrNetworkNameQueryParam)
        ? getValidChainIdFromString(rawChainIdOrNetworkNameQueryParam)
        : undefined;
  } catch {
    // An invalid chain query parameter does not resolve to a token.
  }

  return address && chainId
    ? {
        address,
        chainId,
      }
    : undefined;
};

/**
 * Returns the URL for a token logo.
 * @param address - The address of the token
 * @param chainId - The chain ID of the token
 * @returns The URL for the token logo
 */
export const getTokenLogoURL = (address?: string, chainId?: number) => {
  const defaultLogoURL = getBaseURL() + '/images/liquidity-providers/default.png';
  if (address && chainId) {
    return `https://matcha-image-proxy.api.0x.org/images/${chainId}/${address}`;
  }
  return defaultLogoURL;
};
